import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveReviewAutomationSettings } from "../api/saveReviewAutomationSettings";
import {
  ReviewAutomationSettingsPayload,
  ReviewAutomationSettingsResponse
} from "../../types/review-automation.type";

import { posthogService, PostHogEvent } from "src/common/services/posthogService";

interface UseSaveReviewAutomationSettingsProps {
  onSuccess?: (data: ReviewAutomationSettingsResponse) => void;
  onError?: (error: any) => void;
}

/**
 * Custom React Query mutation hook to save review automation settings.
 *
 * @param props - Optional callbacks for onSuccess and onError, and optional siteIdOverride
 * @returns Object containing the mutation state, handlers, and status flags
 */
export function useSaveReviewAutomationSettings({
  onSuccess,
  onError,
}: UseSaveReviewAutomationSettingsProps = {}) {
  const queryClient = useQueryClient();

  const saveSettingsMutation = useMutation({
    mutationFn: (payload: ReviewAutomationSettingsPayload & { source?: string }) =>
      saveReviewAutomationSettings({
        site_id: payload.site_id,
        ...payload,
      }),
    mutationKey: ["saveReviewAutomationSettings"],
    onSuccess: (data, variables) => {
      const source = (variables as any)?.source || 'review_automation_dashboard';

      // Track Orbit AI Review Automation Agent toggle status in PostHog
      if (variables?.enabled !== undefined) {
        posthogService.track(PostHogEvent.ORBIT_AI_AGENT_TOGGLED, {
          agent_name: 'review_automation',
          enabled: variables.enabled,
          site_id: variables.site_id,
          source,
          config_summary: ['review_settings']
        });
      }

      // Track Orbit AI Review Automation settings saved event in PostHog
      // Captures auto-reply enabled flag, tone, personalization, filters, and trigger source
      posthogService.track(PostHogEvent.ORBIT_AI_REVIEW_SETTINGS_SAVED, {
        site_id: variables.site_id,
        auto_reply_enabled: variables.enabled,
        tone: variables.tone,
        personalize_with_name: variables.personalize_with_name,
        skip_5_star: variables.skip_5_star,
        skip_short_reviews: variables.skip_short_reviews,
        require_approval_for_1_2_star: variables.require_approval_for_1_2_star,
        source
      });

      queryClient.invalidateQueries({
        queryKey: ["getReviewAutomationDashboard"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getReviewAutomationSettings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["reviewAutomationSettings"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getUserAgentDetails"],
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    saveSettingsMutation,
    saveSettings: saveSettingsMutation.mutate,
    saveSettingsAsync: saveSettingsMutation.mutateAsync,
    isLoading: saveSettingsMutation.isPending,
    isSuccess: saveSettingsMutation.isSuccess,
    isError: saveSettingsMutation.isError,
    error: saveSettingsMutation.error,
  };
}

export default useSaveReviewAutomationSettings;
